import { Package } from 'lucide-react';
import { EmptyState, ErrorState, Skeleton, SubBar } from '@/design/components';
import { useMe } from '@/auth/useMe';
import { useImageUrl } from './api';
import {
  usePlanograms,
  type PlanogramShelf,
  type PlanogramSlot,
} from './admin/planogramApi';

/**
 * הפלנוגרמה של הסניף, לעובד.
 *
 * קריאה בלבד: זה מפרט ולא מסך עבודה. מה שהעובד צריך הוא לראות
 * איך המדף אמור להיראות בזמן שהוא מסדר אותו, ולכן התמונות כאן
 * ולא רק השמות — מי שמסדר מחפש את האריזה, לא את הכיתוב.
 *
 * אותו איור כמו בניהול, בלי כלי העריכה.
 */
export function PlanogramView() {
  const { me } = useMe();
  const branchId = me?.primaryBranchId ?? null;
  const board = usePlanograms(branchId);

  return (
    <div className="grid content-start gap-4 pb-8">
      <SubBar title="פלנוגרמה" subtitle="איך המדפים אמורים להיראות בסניף" />

      <div className="grid gap-4 px-4">
      {!branchId && (
        <EmptyState
          title="אין שיוך לסניף"
          description="כשתשויך/י לסניף תראה/י כאן את המדפים שלו."
        />
      )}

      {board.isPending && branchId && <Skeleton className="h-64" />}
      {board.isError && <ErrorState onRetry={() => void board.refetch()} />}

      {board.data?.length === 0 && (
        <EmptyState
          title="עוד לא הוגדרה פלנוגרמה"
          description="כשההנהלה תגדיר את המדפים הם יופיעו כאן."
        />
      )}

      {(board.data ?? []).map((unit) => (
        <section key={unit.id} className="grid gap-2">
          <div className="grid gap-0.5">
            <h2 className="m-0 text-title font-medium text-ink">{unit.name}</h2>
            {unit.note && <p className="m-0 text-body-sm text-muted">{unit.note}</p>}
          </div>

          <div className="grid gap-1 rounded-md border border-line bg-cream p-2">
            {unit.shelves.map((shelf, index) => (
              <ShelfRow key={shelf.id} shelf={shelf} index={index} />
            ))}
          </div>
        </section>
      ))}
      </div>
    </div>
  );
}

/** כמו בניהול: פנים כפול מידה קבועה, כדי שמקום פנוי ייראה פנוי. */
const FACING_WIDTH = 72;

function ShelfRow({ shelf, index }: { shelf: PlanogramShelf; index: number }) {
  return (
    <div className="grid gap-1">
      <span className="text-label text-muted">{shelf.label ?? `מדף ${index + 1}`}</span>

      <div className="flex min-h-28 items-end gap-1 rounded-t-sm border-b-4 border-charcoal bg-surface px-1 pt-1">
        {shelf.slots.length === 0 ? (
          <span className="my-2 grid w-full place-items-center rounded-sm border border-dashed border-line py-5 text-label text-muted">
            מדף ריק
          </span>
        ) : (
          shelf.slots.map((slot) => <SlotCard key={slot.id} slot={slot} />)
        )}
      </div>
    </div>
  );
}

function SlotCard({ slot }: { slot: PlanogramSlot }) {
  const image = useImageUrl(slot.image_path);

  return (
    <div
      className="grid min-w-0 content-end gap-1 rounded-sm border border-line bg-cream p-1"
      style={{ flex: `0 1 ${slot.facings * FACING_WIDTH}px` }}
    >
      {image.data ? (
        <img
          src={image.data}
          alt=""
          className="h-14 w-full rounded-sm object-contain"
          loading="lazy"
        />
      ) : (
        <span className="grid h-14 place-items-center rounded-sm bg-olive-soft text-muted">
          <Package size={18} strokeWidth={1.75} aria-hidden="true" />
        </span>
      )}

      <span className="line-clamp-2 text-label font-medium break-words text-ink">
        {slot.product}
      </span>

      {slot.facings > 1 && (
        <span className="tabular text-label text-muted">{slot.facings} פנים</span>
      )}

      {slot.note && <span className="text-label text-muted">{slot.note}</span>}
    </div>
  );
}
